import React from 'react';
import { Container } from '../container/container';
import styles from './subpage-hero.module.css';
import { HeroMotif, HeroVisual } from './hero-motif';
import { MobileHeroBackdrop } from './mobile-hero-backdrop';

interface SubpageHeroProps {
    title: React.ReactNode;
    beforeTitle?: React.ReactNode;
    description?: React.ReactNode;
    children?: React.ReactNode;
    visual?: HeroVisual;
    illustration?: React.ReactNode;
}

const HeroIllustration: React.FC<{ kind: NonNullable<SubpageHeroProps['visual']> }> = ({ kind }) => {
    return (
        <div className={styles.scene}>
            <svg aria-hidden="true" viewBox="35 0 390 300" fill="none" className={styles.orbit}>
                <circle cx="244" cy="148" r="129" stroke="#d9cdc5" strokeDasharray="4 10" />
            </svg>
            <div className={styles.glass} />
            <svg aria-hidden="true" viewBox="35 0 390 300" fill="none" className={styles.illustration}>
                <rect pathLength="1" x="58" y="40" width="320" height="214" rx="18" fill="none" stroke="#d9cdc5" />
                <path d="M58 78H378" stroke="#d9cdc5" />
                <circle cx="78" cy="59" r="3" fill="#d9cdc5" />
                <circle cx="90" cy="59" r="3" fill="#d9cdc5" />
                <circle cx="102" cy="59" r="3" fill="#d9cdc5" />
                <HeroMotif kind={kind} />
            </svg>
        </div>
    );
};

export const SubpageHero: React.FC<SubpageHeroProps> = ({
    title,
    beforeTitle,
    description,
    children,
    visual = 'help',
    illustration,
}) => (
    <section className="relative isolate py-[5.6rem] vl:flex vl:min-h-[min(68rem,calc(100svh-9rem))] vl:items-center vl:py-[8rem]">
        <MobileHeroBackdrop />
        <Container>
            <div className="grid min-w-0 items-center gap-[3.2rem] vl:grid-cols-[1fr_1.1fr] vl:gap-[6rem]">
                <div className="min-w-0">
                    {beforeTitle && <div className="mb-[2.8rem]">{beforeTitle}</div>}
                    <h1 className="max-w-[72rem] font-serif text-[clamp(3.4rem,10vw,4.4rem)] font-normal leading-[1.04] tracking-[-0.045em] text-brand-secondary sm:text-[5rem] xl:text-[6.4rem] [&_span]:text-inherit">
                        {title}
                    </h1>
                    {description && (
                        <div className="mt-[2.4rem] max-w-[52rem] text-[1.7rem] leading-[1.6] vl:text-[1.9rem] text-web-copy [&_strong]:font-semibold">
                            {description}
                        </div>
                    )}
                    {children && <div className="mt-[3.2rem] flex flex-wrap gap-[1.2rem]">{children}</div>}
                </div>
                <div className="mx-auto w-full max-w-[52rem] vl:max-w-[68rem]">
                    {illustration ?? <HeroIllustration kind={visual} />}
                </div>
            </div>
        </Container>
    </section>
);
